/**
 * Gateway chargebacks and their response packs (payments doc 15 §8), on the risk screen.
 *
 * **Nearest deadline first.** The server orders by `respond_by`; a dispute with no deadline sorts
 * last and is flagged, because a missing deadline is an incident, not a free pass.
 *
 * **The pack is read-only here.** It is assembled by the server from rows we already hold, the
 * sections that lead for the dispute's reason first, and it arrives masked — no full phone, no
 * address line, no OTP material, no evidence key. Evidence goes in through the gateway's own
 * dashboard; this screen is what a reviewer reads before doing that. The drawn copy (PDF + JSON zip)
 * downloads through a short-lived link, asked for at the click because it expires.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { Toggle } from '../components/forms';
import { DataTable, Pager } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import {
  useChargeback,
  fetchChargebackPack,
  useChargebacks,
  type ChargebackRow,
  type PackSection,
} from '../lib/api/hooks/risk';
import { formatDateTime } from '../lib/datetime';

const label = (value: string) => value.replace(/_/g, ' ');
const rupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/** What a dispute's status means to somebody scanning the column. */
export function chargebackTone(status: ChargebackRow['status']): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'won') return 'success';
  if (status === 'lost' || status === 'accepted') return 'error';
  if (status === 'evidence_submitted') return 'warn';
  return 'neutral';
}

/** One fact as a line: `key value · key value`. Nested objects (the venue) flatten one level. */
export function factLine(fact: Record<string, unknown>): string {
  return Object.entries(fact)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([k, v]) =>
      typeof v === 'object'
        ? factLine(v as Record<string, unknown>)
        : `${label(k)} ${String(v)}`,
    )
    .filter(Boolean)
    .join(' · ');
}

function Pack({ sections }: { sections: PackSection[] }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      {sections.map((s) => (
        <div key={s.key} data-testid={`pack-${s.key}`}>
          <strong>{label(s.key)}</strong>
          {s.lead && <StatusChip tone="warn" label="leads" />}
          {s.facts.length === 0 ? (
            <div>Nothing on record.</div>
          ) : (
            <ul>
              {s.facts.map((fact, i) => (
                <li key={i}>{factLine(fact)}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function ChargebackDetail({ code, onClose }: { code: string; onClose: () => void }) {
  const detail = useChargeback(code);
  const [fetching, setFetching] = useState(false);
  const [packNote, setPackNote] = useState<string | null>(null);
  const download = async () => {
    setFetching(true);
    try {
      const answer = await fetchChargebackPack(code);
      if ('url' in answer) {
        setPackNote(null);
        window.open(answer.url, '_blank', 'noopener');
      } else {
        setPackNote('The pack is still being drawn. Try again in a few minutes.');
      }
    } catch (error) {
      setPackNote(`Could not fetch the pack: ${errorMessage(error)}`);
    } finally {
      setFetching(false);
    }
  };
  return (
    <Section
      title={code}
      note={detail.data ? `reason ${detail.data.chargeback.reason_code ?? 'not given'}` : undefined}
      actions={
        <>
          <Button variant="secondary" size="sm" disabled={fetching} onClick={download}>
            Download pack
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {packNote && <div role="status">{packNote}</div>}
      {detail.isPending && <LoadingState message="Assembling the pack…" />}
      {detail.isError && <ErrorState title="Could not assemble the pack" cause={errorMessage(detail.error)} />}
      {detail.isSuccess && <Pack sections={detail.data.pack} />}
    </Section>
  );
}

export function Chargebacks() {
  const [includeClosed, setIncludeClosed] = useState(false);
  const [page, setPage] = useState(1);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const list = useChargebacks(includeClosed, page);
  const items = list.data?.items ?? [];
  /* A different filter is a different list: page 4 of the open ones means nothing in all. */
  const filter = (on: boolean) => {
    setIncludeClosed(on);
    setPage(1);
  };

  return (
    <>
      <Section
        title="Chargebacks"
        note="nearest response deadline first"
        actions={<Toggle label="Show closed" on={includeClosed} onChange={filter} />}
        pad={false}
      >
        {list.isPending && <LoadingState message="Reading the disputes…" />}
        {list.isError && <ErrorState title="Could not read the disputes" cause={errorMessage(list.error)} />}
        {list.isSuccess && items.length === 0 && (
          <EmptyState inline icon="shield-check" title="No open disputes" body="No chargeback is waiting on a response." />
        )}
        {items.length > 0 && (
          <DataTable
            density="compact"
            onRowClick={(row) => setOpenCode(String(row.id))}
            columns={[
              { key: 'code', label: 'Dispute', mono: true },
              { key: 'booking', label: 'Booking', mono: true },
              { key: 'amount', label: 'Amount', align: 'right', mono: true },
              { key: 'reason', label: 'Reason' },
              { key: 'status', label: 'Status' },
              { key: 'respond', label: 'Respond by', mono: true },
            ]}
            rows={items.map((c) => ({
              id: c.code,
              code: c.code,
              booking: c.booking_code,
              amount: rupees(c.amount_paise),
              reason: c.reason_code ? label(c.reason_code) : '—',
              status: <StatusChip tone={chargebackTone(c.status)} label={label(c.status)} />,
              respond: c.respond_by ? (
                formatDateTime(c.respond_by)
              ) : (
                <StatusChip tone="error" label="no deadline" />
              ),
            }))}
          />
        )}
        {list.data && (
          <Pager page={page} pageSize={list.data.page_size} total={list.data.total} onPage={setPage} />
        )}
      </Section>
      {openCode && <ChargebackDetail key={openCode} code={openCode} onClose={() => setOpenCode(null)} />}
    </>
  );
}
